import {
  ChevronRightIcon,
  MoreVerticalIcon,
  PencilIcon,
  PlusIcon,
  PowerIcon,
  UsersIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import { Casilla } from '@/components/ui/Campo'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
} from '@/components/ui/MenuDesplegable'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { cn } from '@/lib/cn'
import type { ClienteAdmin } from '@/lib/schemas/admin'
import { useGuardarCliente } from './api'
import { Confirmacion } from './Confirmacion'
import { ModalCliente } from './ModalCliente'
import { Panel, VacioPanel } from './Panel'

interface ColumnaClientesProps {
  clientes: ClienteAdmin[]
  cargando: boolean
  seleccionado: string | null
  alElegir: (id: string) => void
  busqueda: string
  mostrarInactivos: boolean
  alMostrarInactivos: (v: boolean) => void
  /** El botón "+" flotante del celular abre el mismo modal. */
  abrirNuevo: boolean
  alCerrarNuevo: () => void
}

/** P03 · columna Clientes (§8.6). */
export function ColumnaClientes({
  clientes,
  cargando,
  seleccionado,
  alElegir,
  busqueda,
  mostrarInactivos,
  alMostrarInactivos,
  abrirNuevo,
  alCerrarNuevo,
}: ColumnaClientesProps) {
  const [editando, setEditando] = useState<ClienteAdmin | null>(null)
  const [nuevoLocal, setNuevoLocal] = useState(false)
  const [aDesactivar, setADesactivar] = useState<ClienteAdmin | null>(null)
  const guardar = useGuardarCliente()
  const avisar = useAvisar()

  const filtro = busqueda.trim().toLowerCase()
  const visibles = clientes.filter(
    (c) =>
      (mostrarInactivos || c.activo) &&
      (!filtro ||
        c.nombre.toLowerCase().includes(filtro) ||
        c.usuarioInstagram.toLowerCase().includes(filtro)),
  )

  const hayClientes = clientes.length > 0
  const modalAbierto = abrirNuevo || nuevoLocal || editando !== null
  const cerrarModal = () => {
    setNuevoLocal(false)
    setEditando(null)
    alCerrarNuevo()
  }

  return (
    <Panel
      titulo="Clientes"
      accion={
        <Boton
          tamano="chico"
          onClick={() => {
            setNuevoLocal(true)
          }}
        >
          <PlusIcon className="size-4" aria-hidden />
          Nuevo cliente
        </Boton>
      }
      pie={
        hayClientes ? (
          <Casilla
            etiqueta="Mostrar inactivos"
            checked={mostrarInactivos}
            onCheckedChange={(v) => {
              alMostrarInactivos(v === true)
            }}
          />
        ) : undefined
      }
    >
      {cargando ? (
        <p className="px-4 py-6 text-sm text-tinta-suave">Cargando…</p>
      ) : !hayClientes ? (
        <VacioPanel
          icono={<UsersIcon className="size-8" aria-hidden />}
          texto="Todavía no hay clientes. Creá el primero."
        >
          <Boton
            onClick={() => {
              setNuevoLocal(true)
            }}
          >
            <PlusIcon className="size-4" aria-hidden />
            Nuevo cliente
          </Boton>
        </VacioPanel>
      ) : visibles.length === 0 ? (
        <p className="px-4 py-6 text-sm text-tinta-suave">
          Ningún cliente coincide con la búsqueda.
        </p>
      ) : (
        <ul className="flex flex-col gap-1 p-2" aria-label="Clientes">
          {visibles.map((c) => {
            const activo = c.id === seleccionado
            return (
              <li key={c.id} className="flex items-stretch gap-1">
                <button
                  type="button"
                  onClick={() => {
                    alElegir(c.id)
                  }}
                  aria-current={activo ? 'true' : undefined}
                  data-cliente={c.nombre}
                  className={cn(
                    'flex min-h-16 min-w-0 flex-1 items-center gap-3 rounded-2xl px-3 text-left',
                    'hover:bg-lienzo focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tinta',
                    activo && 'bg-lienzo ring-1 ring-borde',
                    !c.activo && 'opacity-60',
                  )}
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-borde text-sm font-semibold">
                    {c.nombre.slice(0, 2).toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{c.nombre}</span>
                    <span className="block truncate text-xs text-tinta-suave">
                      @{c.usuarioInstagram}
                    </span>
                    <span className="block text-xs text-tinta-suave">
                      {c.activo ? proyectosTexto(c.proyectosActivos) : 'Inactivo'}
                    </span>
                  </span>
                  <ChevronRightIcon
                    className="size-4 shrink-0 text-tinta-suave"
                    aria-hidden
                  />
                </button>
                <MenuDesplegable>
                  <MenuDisparador asChild>
                    <Boton
                      variante="sutil"
                      tamano="icono"
                      aria-label={`Opciones de ${c.nombre}`}
                      className="self-center"
                    >
                      <MoreVerticalIcon className="size-5" aria-hidden />
                    </Boton>
                  </MenuDisparador>
                  <MenuContenido>
                    <MenuItem
                      onSelect={() => {
                        setEditando(c)
                      }}
                    >
                      <PencilIcon className="size-4" aria-hidden />
                      Editar
                    </MenuItem>
                    {c.activo ? (
                      <MenuItem
                        peligro
                        onSelect={() => {
                          setADesactivar(c)
                        }}
                      >
                        <PowerIcon className="size-4" aria-hidden />
                        Desactivar
                      </MenuItem>
                    ) : (
                      <MenuItem
                        onSelect={() => {
                          void guardar
                            .mutateAsync({
                              id: c.id,
                              datos: {
                                nombre: c.nombre,
                                usuarioInstagram: c.usuarioInstagram,
                                driveFolder: c.driveFolderId,
                                activo: true,
                              },
                            })
                            .then(() => {
                              avisar(`${c.nombre} reactivado`)
                            })
                        }}
                      >
                        <PowerIcon className="size-4" aria-hidden />
                        Reactivar
                      </MenuItem>
                    )}
                  </MenuContenido>
                </MenuDesplegable>
              </li>
            )
          })}
        </ul>
      )}

      <ModalCliente
        abierto={modalAbierto}
        alCerrar={cerrarModal}
        cliente={editando}
        alGuardar={(c) => {
          if (!editando) alElegir(c.id)
        }}
      />

      <Confirmacion
        abierto={aDesactivar !== null}
        alCerrar={() => {
          setADesactivar(null)
        }}
        titulo="Desactivar cliente"
        texto={`${aDesactivar?.nombre ?? ''} deja de verse en las listas y sus proyectos dejan de estar disponibles para las personas del cliente. No se borra nada.`}
        confirmar="Desactivar"
        peligro
        alConfirmar={async () => {
          if (!aDesactivar) return
          await guardar.mutateAsync({
            id: aDesactivar.id,
            datos: {
              nombre: aDesactivar.nombre,
              usuarioInstagram: aDesactivar.usuarioInstagram,
              driveFolder: aDesactivar.driveFolderId,
              activo: false,
            },
          })
          avisar(`${aDesactivar.nombre} desactivado`)
        }}
      />
    </Panel>
  )
}

const proyectosTexto = (n: number) =>
  n === 0
    ? 'Sin proyectos activos'
    : n === 1
      ? '1 proyecto activo'
      : `${String(n)} proyectos activos`
